import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaCheckCircle } from 'react-icons/fa';
import { usePageContent } from '../services/pageContentService';
import HtmlContent from '../components/HtmlContent';
import RawHtmlPage, { isRawHtmlPage } from '../components/RawHtmlPage';

const AboutTheJournal = () => {
  const location = useLocation();
  const { content, loading } = usePageContent('about_us');

  // Smooth scroll to anchor if hash is present in URL
  useEffect(() => {
    if (location.hash) {
      const hashId = location.hash.replace('#', '');
      const timer = setTimeout(() => {
        const el = document.getElementById(hashId);
        if (el) {
          const header = document.querySelector('header');
          const headerHeight = header ? header.offsetHeight : 140;
          const elementPosition = el.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - (headerHeight + 20);
          window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth'
          });
        }
      }, 120);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  const titles = content?.titles || {};

  // Wait for the saved text rather than flashing the built-in default first
  if (loading) return <div className="min-h-screen" />;

  if (isRawHtmlPage(content)) {
    return <RawHtmlPage html={content.full_content} title={content?.titles?.page_title || 'About the Journal'} />;
  }

  return (
    <div className="bg-[#FAF9F6] min-h-screen text-gray-800 font-sans pb-16">
      {/* Breadcrumb Bar */}
      <div className="bg-white py-3.5 px-4 sm:px-8 text-xs text-gray-500 shadow-xs">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="text-[#03478E] hover:text-[#023366] hover:underline font-medium">
            Home
          </Link>
          <span>&gt;</span>
          <span className="font-semibold text-gray-700">About</span>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <main className="bg-white rounded-3xl p-8 sm:p-12 shadow-sm space-y-10">
          <div>
            <span className="text-xs font-bold text-[#03478E] uppercase tracking-widest block mb-1">
              JOURNAL PROFILE
            </span>
            <h1 className="page-title">
              {titles.page_title || content?.page_title || 'About the Journal'}
            </h1>
            {content?.overview && (
              <div className="mt-4 text-sm sm:text-base text-gray-600 leading-relaxed border-l-4 border-[#03478E] pl-4 bg-blue-50/40 py-2 rounded-r-xl">
                <HtmlContent content={content.overview} />
              </div>
            )}
          </div>

          {/* ================= FULL PAGE TEXT (IF ENTERED AS SINGLE DOCUMENT) ================= */}
          {content?.full_content ? (
            <div className="prose max-w-none text-gray-800 leading-relaxed space-y-6 border-t border-gray-100 pt-6">
              <HtmlContent content={content.full_content} />
            </div>
          ) : (
            /* ================= COHESIVE SINGLE PAGE SECTIONS (ALL OPEN) ================= */
            <div className="space-y-10">
              
              {/* Section 2.1: Mission, Vision & Scope */}
              <section
                id="mission-vision-scope"
                className="scroll-mt-28 sm:scroll-mt-32 space-y-6 pt-2 border-t border-gray-100"
              >
                <div className="pb-2 border-b border-gray-100">
                  <h2 className="section-heading">
                    {titles.section_2_1 || 'Mission, Vision & Scope'}
                  </h2>
                </div>

                <div className="space-y-6">
                  {/* Mission */}
                  <div className="space-y-2.5">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-[#03478E]">
                      {titles.mission || 'Mission'}
                    </h3>
                    <HtmlContent
                      content={content?.mission}
                      fallback="To publish high-quality, peer-reviewed clinical, pharmacological, and philosophical scientific research that contributes to the advancement of homoeopathy, patient safety, and evidence-informed integrative healthcare globally."
                      className="text-sm sm:text-base text-gray-700 leading-relaxed"
                    />
                    {Array.isArray(content?.mission_bullets) && content.mission_bullets.length > 0 && (
                      <ul className="space-y-2 text-gray-700 text-xs sm:text-sm pt-2">
                        {content.mission_bullets.map((bullet, bIdx) => (
                          <li key={bIdx} className="flex items-start gap-2.5 bg-[#FAFBFD] p-3.5 rounded-xl border border-gray-100">
                            <FaCheckCircle className="text-[#03478E] text-sm mt-0.5 flex-shrink-0" />
                            <HtmlContent content={bullet} as="span" className="flex-1" />
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Vision */}
                  <div className="space-y-2.5">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-[#03478E]">
                      {titles.vision || 'Vision'}
                    </h3>
                    <HtmlContent
                      content={content?.vision}
                      fallback="To become a premier international homoeopathic journal indexed in leading global biomedical databases, bridging classical Hahnemannian principles and modern biomedical research methodologies."
                      className="text-sm sm:text-base text-gray-700 leading-relaxed bg-[#FAFBFD] p-4 rounded-xl border border-gray-100"
                    />
                  </div>

                  {/* Scope */}
                  <div className="space-y-2.5">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-[#03478E]">
                      {titles.scope || 'Scope Overview'}
                    </h3>
                    <HtmlContent
                      content={content?.scope}
                      fallback="The scope includes clinical homeopathy, repertory research, homoeopathic materia medica, ultra-high dilution pharmacology, patient-reported outcome measures, and integrative clinical trials."
                      className="text-sm sm:text-base text-gray-700 leading-relaxed"
                    />
                  </div>

                  {/* Editorial Policy */}
                  <div className="space-y-2.5">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-[#03478E]">
                      {titles.editorial_policy || 'Editorial Policy'}
                    </h3>
                    <HtmlContent
                      content={content?.editorial_policy}
                      fallback="All manuscripts are assessed solely on scientific rigor, clinical relevance, and ethical compliance. Editorial decisions are independent of political, commercial, or institutional influence."
                      className="text-sm sm:text-base text-gray-700 leading-relaxed"
                    />
                  </div>

                  {/* Who can submit */}
                  <div className="space-y-2.5">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-[#03478E]">
                      {titles.who_can_submit || 'Who Can Submit?'}
                    </h3>
                    <HtmlContent
                      content={content?.who_can_submit}
                      fallback="Homoeopathic practitioners, postgraduate scholars, academicians, pharmacologists, integrative medicine researchers, and healthcare professionals from India and across the world are warmly invited to submit manuscripts."
                      className="text-sm sm:text-base text-gray-700 leading-relaxed"
                    />
                  </div>
                </div>
              </section>

              {/* Section 2.2: Journal Information, Publication History */}
              <section
                id="journal-info"
                className="scroll-mt-28 sm:scroll-mt-32 space-y-6 pt-4 border-t border-gray-100"
              >
                <div className="pb-2 border-b border-gray-100">
                  <h2 className="section-heading">
                    {titles.section_2_2 || 'Journal Information & Publication History'}
                  </h2>
                </div>

                <div className="space-y-3 text-xs sm:text-sm">
                  <div className="bg-[#FAFBFD] p-5 rounded-2xl border border-gray-100 space-y-1.5">
                    <span className="text-gray-500 font-semibold uppercase text-xs tracking-wider">
                      {titles.journal_title || 'Journal Title'}
                    </span>
                    <HtmlContent content={content?.journal_title} fallback="AIM Journal of Homoeopathic Medicine (AJHM)" className="font-bold text-gray-900 text-sm sm:text-base" as="p" />
                  </div>
                  <div className="bg-[#FAFBFD] p-5 rounded-2xl border border-gray-100 space-y-1.5">
                    <span className="text-gray-500 font-semibold uppercase text-xs tracking-wider">
                      {titles.journal_frequency || 'Publication Frequency'}
                    </span>
                    <HtmlContent content={content?.journal_frequency} fallback="Biannual (January–June, July–December)" className="font-bold text-gray-900 text-sm sm:text-base" as="p" />
                  </div>
                  <div className="bg-[#FAFBFD] p-5 rounded-2xl border border-gray-100 space-y-1.5">
                    <span className="text-gray-500 font-semibold uppercase text-xs tracking-wider">
                      {titles.journal_model || 'Publishing Model'}
                    </span>
                    <HtmlContent content={content?.journal_model} fallback="Open Access, Peer-Reviewed" className="font-bold text-gray-900 text-sm sm:text-base" as="p" />
                  </div>
                  <div className="bg-[#FAFBFD] p-5 rounded-2xl border border-gray-100 space-y-1.5">
                    <span className="text-gray-500 font-semibold uppercase text-xs tracking-wider">
                      {titles.journal_history || 'Publication History'}
                    </span>
                    <HtmlContent content={content?.journal_history} fallback="Established 2025 by Domus Publications" className="font-bold text-gray-900 text-sm sm:text-base" as="p" />
                  </div>
                </div>
              </section>

              {/* Section 2.3: Publisher & Institutional Information */}
              <section
                id="publisher-info"
                className="scroll-mt-28 sm:scroll-mt-32 space-y-6 pt-4 border-t border-gray-100"
              >
                <div className="pb-2 border-b border-gray-100">
                  <h2 className="section-heading">
                    {titles.section_2_3 || 'Publisher & Institutional Information'}
                  </h2>
                </div>

                <div className="bg-[#FAFBFD] p-6 rounded-2xl border border-gray-100 space-y-4 text-sm text-gray-700 leading-relaxed">
                  <div>
                    <span className="text-gray-500 font-semibold uppercase text-xs tracking-wider block mb-1">
                      Publisher & Legal Entity
                    </span>
                    <p className="font-bold text-gray-900 text-base">
                      <HtmlContent content={content?.publisher_name} fallback="Domus Publications, an academic publishing imprint of Domus Mediverse Private Limited." as="span" />
                    </p>
                  </div>
                  <div className="border-t border-gray-200/60 pt-3">
                    <span className="text-gray-500 font-semibold uppercase text-xs tracking-wider block mb-1">
                      Registered Address
                    </span>
                    <p>
                      <HtmlContent content={content?.publisher_address} fallback="Hill View Colony, Near Chandra Vatika, Khatima, Udham Singh Nagar - 262308, Uttarakhand, India." as="span" />
                    </p>
                  </div>
                  <div className="border-t border-gray-200/60 pt-3">
                    <span className="text-gray-500 font-semibold uppercase text-xs tracking-wider block mb-1">
                      Institutional Affiliation & Academic Collaboration
                    </span>
                    <p>
                      <HtmlContent content={content?.publisher_affiliation} fallback="In scholarly collaboration with academic faculties and homoeopathic medical colleges advancing scientific homoeopathic education." as="span" />
                    </p>
                  </div>
                </div>
              </section>

            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AboutTheJournal;
